import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, apiPut } from '../lib/api'
import { createLayoutActions, type LayoutActions } from './layoutActions'
// @ts-ignore
import type ReactGridLayout from 'react-grid-layout'
import { EMPLOYEE_PERFORMANCE_XS_ROWS, type EmployeePerformanceSettings } from '../lib/employeePerformance'

// ── Types ────────────────────────────────────────────────────────────

export interface WidgetSettings extends EmployeePerformanceSettings {
    showLastMonth?: boolean
    showLastYear?: boolean
    showPctChange?: boolean
    showNetChange?: boolean
    chartCompareTo?: 'none' | 'lastMonth' | 'lastYear'
    showDiscounts?: boolean
    sortBy?: 'name' | 'number' | 'performance'
}

export interface WidgetMeta {
    id: string
    label: string
    description: string
    category: 'KPI' | 'Chart' | 'Table'
    dashboardTypes: string
    defaultW: number
    defaultH: number
    isEnabled: boolean
}

/** Default dimensions for each widget at full size (based on 40 columns) */
export const DEFAULT_WIDGET_DIMS: Record<string, { w: number, h: number }> = {
    netSales: { w: 10, h: 10 }, margin: { w: 10, h: 10 }, costOfSales: { w: 10, h: 10 }, transactions: { w: 10, h: 10 },
    unitsSold: { w: 10, h: 10 }, avgTransaction: { w: 10, h: 10 }, upt: { w: 10, h: 10 }, discountDepth: { w: 10, h: 10 },
    sellThrough: { w: 10, h: 10 }, inventoryValue: { w: 10, h: 10 }, gmroi: { w: 10, h: 10 },
    channelMix: { w: 40, h: 20 }, hourlyChart: { w: 30, h: 20 }, paymentMix: { w: 10, h: 10 },
    topSellers: { w: 40, h: 20 }, soldOut: { w: 40, h: 20 },
    performanceChart: { w: 40, h: 20 },
    salesByStore: { w: 40, h: 20 },
    employeePerformance: { w: 40, h: 24 },
}

/** Phone (xs) heights for widgets that need more than their usual mobile height */
export const DEFAULT_XS_H: Record<string, number> = {
    employeePerformance: EMPLOYEE_PERFORMANCE_XS_ROWS,
}

/** Compute actual dimensions considering dynamic size */
export function getWidgetDims(widgetId: string): { w: number, h: number } {
    let def = DEFAULT_WIDGET_DIMS[widgetId]

    if (!def) {
        const meta = useCustomRangeDashboardLayoutStore.getState().availableWidgets?.find(w => w.id === widgetId)
        if (meta) {
            def = { w: Math.round(meta.defaultW * 2.5), h: Math.round(meta.defaultH * 2.5) }
        } else {
            def = { w: 10, h: 10 }
        }
    }

    return def
}

export interface DashboardLayoutState extends LayoutActions {
    layouts: Record<string, ReactGridLayout.Layout[]>
    activeWidgets: string[]
    widgetSettings: Record<string, WidgetSettings>
    isEditing: boolean
    availableWidgets: WidgetMeta[]
    primaryComparison: 'none' | 'lastMonth' | 'lastYear'
    /** When true, KPI comparisons use the full period instead of period-to-date */
    compareFull: boolean
    datePreset: string | null
    compareMode: string
    customCompareStart: string
    customCompareEnd: string
    loaded: boolean
    
    availableLayouts: DashboardLayoutPreset[]
    currentLayoutId: string | null
    currentLayoutName: string | null
    currentLayoutIsDefault: boolean
    /** When the layout on screen was last saved, and when the system default last was */
    currentLayoutUpdatedAt: string | null
    systemLayoutUpdatedAt: string | null

    storedUserId: string | null
    previousLayouts: Record<string, ReactGridLayout.Layout[]> | null
    preEditState: {
        layouts: Record<string, ReactGridLayout.Layout[]>
        activeWidgets: string[]
        widgetSettings: Record<string, WidgetSettings>
        primaryComparison: 'none' | 'lastMonth' | 'lastYear'
        compareFull: boolean
        datePreset: string | null
        compareMode: string
        customCompareStart: string
        customCompareEnd: string
    } | null
    layoutVersion: number

    clearUserState: () => void
    setEditing: (editing: boolean) => void
    addWidget: (id: string) => void
    removeWidget: (id: string) => void
    setLayouts: (layouts: Record<string, ReactGridLayout.Layout[]>) => void
    snapshotLayouts: () => void
    undoLayouts: () => void
    revertToInitial: () => void
    updateWidgetSettings: (id: string, settings: Partial<WidgetSettings>) => void
    setPrimaryComparison: (comp: 'none' | 'lastMonth' | 'lastYear') => void
    setCompareFull: (val: boolean) => void
    setDateSettings: (preset: string | null, compare: string, customStart: string, customEnd: string) => void
    resetLayout: () => void
    setAvailableWidgets: (widgets: WidgetMeta[]) => void
    loadFromServer: (token: string, userId: string, dashboardType?: string) => Promise<void>
    loadLayout: (token: string, userId: string, dashboardType?: string, id?: string | null) => Promise<void>
    saveAsSystemDefault: (token: string, dashboardType?: string, overwriteUsers?: boolean) => Promise<void>
    hasUnsavedChanges: () => boolean
    applyImportedLayout: (parsed: { layouts: Record<string, any[]>; activeWidgets: string[]; widgetSettings: Record<string, WidgetSettings>; primaryComparison?: string; compareFull?: boolean }) => void
}

export interface DashboardLayoutPreset {
    id: string
    name: string | null
    isDefault: boolean
    sortOrder: number
}

// ── Default layout ───────────────────────────────────────────────────

const DEFAULT_ACTIVE_WIDGETS = [
    'performanceChart',
    'netSales', 'margin', 'costOfSales', 'transactions',
    'unitsSold', 'avgTransaction', 'upt', 'discountDepth',
    'sellThrough', 'inventoryValue', 'gmroi',
    'channelMix', 'paymentMix',
    'topSellers', 'soldOut', 'salesByStore',
    'employeePerformance'
]

const DEFAULT_WIDGET_SETTINGS: Record<string, WidgetSettings> = {}
DEFAULT_ACTIVE_WIDGETS.forEach(id => {
    DEFAULT_WIDGET_SETTINGS[id] = {
        showLastMonth: true,
        showLastYear: true,
        showPctChange: true,
        showNetChange: true,
        chartCompareTo: 'lastMonth',
        showDiscounts: true
    }
})

export function generateDefaultLayouts(): Record<string, ReactGridLayout.Layout[]> {
    const lg: ReactGridLayout.Layout[] = []
    const xs: ReactGridLayout.Layout[] = []
    let xl = 0, yl = 0, xx = 0, yx = 0
    DEFAULT_ACTIVE_WIDGETS.forEach((id) => {
        const dims = getWidgetDims(id)
        if (xl + dims.w > 40) { xl = 0; yl += dims.h }
        lg.push({ i: id, x: xl, y: yl, w: dims.w, h: dims.h })
        xl += dims.w

        const xw = dims.w >= 20 ? 10 : 5
        const rowH = dims.h >= 20 ? 10 : Math.max(5, Math.ceil(dims.h / 2))
        // A taller phone height only grows the widget itself: the row step stays the usual one, so it
        // sits right under the row before it (it is last in the list, so nothing stacks under it).
        const xh = DEFAULT_XS_H[id] ?? rowH
        if (xx + xw > 10) { xx = 0; yx += rowH }
        xs.push({ i: id, x: xx, y: yx, w: xw, h: xh })
        xx += xw
    })
    return { lg, xs }
}

/**
 * Persist v3: adds Employee Performance to a layout cached before the widget existed — full width,
 * under everything else on the desktop grid (MonthlyWidgetGrid fills in its phone item). Daily and
 * Monthly don't need this because a server system default always replaces their cache; nothing is
 * seeded for Custom range, so without one a returning browser's old list would win for good. It
 * runs once, on the upgrade, so a user who removes the widget afterwards keeps it removed.
 */
export function appendEmployeePerformance(
    layouts: Record<string, ReactGridLayout.Layout[]>,
    activeWidgets: string[],
): { layouts: Record<string, ReactGridLayout.Layout[]>; activeWidgets: string[] } {
    const id = 'employeePerformance'
    if (activeWidgets.includes(id)) return { layouts, activeWidgets }
    const lg = (layouts.lg ?? []).filter(l => l.i !== id)
    const bottom = lg.reduce((max, l) => Math.max(max, l.y + l.h), 0)
    const { w, h } = DEFAULT_WIDGET_DIMS[id]
    return {
        activeWidgets: [...activeWidgets, id],
        layouts: { ...layouts, lg: [...lg, { i: id, x: 0, y: bottom, w, h }] },
    }
}


// ── Store ────────────────────────────────────────────────────────────

export const useCustomRangeDashboardLayoutStore = create<DashboardLayoutState>()(
    persist(
        (set, get) => ({
            layouts: generateDefaultLayouts(),
            activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
            widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
            primaryComparison: 'none',
            compareFull: false,
            datePreset: 'last90',
            compareMode: 'none',
            customCompareStart: '',
            customCompareEnd: '',
            isEditing: false,
            availableWidgets: [],
            loaded: false,
            availableLayouts: [],
            currentLayoutId: null,
            currentLayoutName: null,
            currentLayoutIsDefault: false,
            currentLayoutUpdatedAt: null,
            systemLayoutUpdatedAt: null,
            storedUserId: null,
            previousLayouts: null,
            preEditState: null,
            layoutVersion: 0,

            clearUserState: () => set((s) => ({
                layouts: generateDefaultLayouts(),
                activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
                widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
                primaryComparison: 'none',
                compareFull: false,
                datePreset: 'last90',
                compareMode: 'none',
                customCompareStart: '',
                customCompareEnd: '',
                isEditing: false,
                availableWidgets: [],
                loaded: false,
                availableLayouts: [],
                currentLayoutId: null,
                currentLayoutName: null,
                currentLayoutIsDefault: false,
                currentLayoutUpdatedAt: null,
                systemLayoutUpdatedAt: null,
                storedUserId: null,
                previousLayouts: null,
                preEditState: null,
                layoutVersion: s.layoutVersion + 1,
            })),

            setEditing: (editing) => {
                const state = get()
                if (editing && !state.isEditing) {
                    set({
                        isEditing: true,
                        preEditState: {
                            layouts: JSON.parse(JSON.stringify(state.layouts)),
                            activeWidgets: [...state.activeWidgets],
                            widgetSettings: JSON.parse(JSON.stringify(state.widgetSettings)),
                            primaryComparison: state.primaryComparison,
                            compareFull: state.compareFull,
                            datePreset: state.datePreset,
                            compareMode: state.compareMode,
                            customCompareStart: state.customCompareStart,
                            customCompareEnd: state.customCompareEnd,
                        }
                    })
                } else if (!editing) {
                    set({ isEditing: false, preEditState: null })
                }
            },

            addWidget: (id) => {
                const state = get()
                if (state.activeWidgets.includes(id)) return

                const lgLayouts = state.layouts.lg || []
                const maxY = lgLayouts.reduce((max, l) => Math.max(max, l.y + l.h), 0)
                const dims = getWidgetDims(id)
                const newLayoutItem = { i: id, x: 0, y: maxY, w: dims.w, h: dims.h }

                const xsLayouts = state.layouts.xs || []
                const maxYxs = xsLayouts.reduce((max, l) => Math.max(max, l.y + l.h), 0)
                const xw = dims.w >= 20 ? 10 : 5
                const xh = DEFAULT_XS_H[id] ?? (dims.h >= 20 ? 10 : Math.max(5, Math.ceil(dims.h / 2)))
                const newXsItem = { i: id, x: 0, y: maxYxs, w: xw, h: xh }

                set({
                    activeWidgets: [...state.activeWidgets, id],
                    layouts: {
                        ...state.layouts,
                        lg: [...lgLayouts, newLayoutItem],
                        xs: [...xsLayouts, newXsItem]
                    },
                    widgetSettings: {
                        ...state.widgetSettings,
                        [id]: { showLastMonth: true, showLastYear: true },
                    },
                })
            },

            removeWidget: (id) => {
                const state = get()
                set({
                    activeWidgets: state.activeWidgets.filter(w => w !== id),
                    layouts: {
                        ...state.layouts,
                        lg: (state.layouts.lg || []).filter(l => l.i !== id),
                        xs: (state.layouts.xs || []).filter(l => l.i !== id)
                    }
                })
            },

            setLayouts: (layouts) => set({ layouts }),

            snapshotLayouts: () => {
                const state = get()
                set({ previousLayouts: JSON.parse(JSON.stringify(state.layouts)) })
            },

            undoLayouts: () => {
                const state = get()
                if (state.previousLayouts) {
                    set({ layouts: state.previousLayouts, previousLayouts: null })
                }
            },

            revertToInitial: () => {
                const state = get()
                if (state.preEditState) {
                    set({
                        layouts: state.preEditState.layouts,
                        activeWidgets: state.preEditState.activeWidgets,
                        widgetSettings: state.preEditState.widgetSettings,
                        primaryComparison: state.preEditState.primaryComparison,
                        compareFull: state.preEditState.compareFull,
                        datePreset: state.preEditState.datePreset,
                        compareMode: state.preEditState.compareMode,
                        customCompareStart: state.preEditState.customCompareStart,
                        customCompareEnd: state.preEditState.customCompareEnd,
                        isEditing: false,
                        preEditState: null,
                    })
                }
            },

            updateWidgetSettings: (id, settings) => {
                const state = get()
                set({
                    widgetSettings: {
                        ...state.widgetSettings,
                        [id]: { ...state.widgetSettings[id], ...settings },
                    },
                })
            },

            setPrimaryComparison: (comp) => set({ primaryComparison: comp }),
            setCompareFull: (val) => set({ compareFull: val }),

            setDateSettings: (preset, compare, customStart, customEnd) => set({
                datePreset: preset,
                compareMode: compare,
                customCompareStart: customStart,
                customCompareEnd: customEnd,
            }),

            resetLayout: () => set({
                layouts: generateDefaultLayouts(),
                activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
                widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
                primaryComparison: 'none',
                compareFull: false,
                datePreset: 'last90',
                compareMode: 'none',
                customCompareStart: '',
                customCompareEnd: '',
                previousLayouts: null,
                preEditState: null,
            }),

            setAvailableWidgets: (widgets) => set({ availableWidgets: widgets }),

            loadFromServer: async (token, userId, dashboardType = 'custom-range') => {
                try {
                    const widgetData = await api(`/api/dashboard/widgets?type=${dashboardType}`, token)
                    if (widgetData?.widgets) {
                        set({ availableWidgets: widgetData.widgets })
                    }

                    const state = get()
                    const userChanged = state.storedUserId !== null && state.storedUserId !== userId
                    if (userChanged) {
                        set({
                            layouts: generateDefaultLayouts(),
                            activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
                            widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
                            primaryComparison: 'none',
                            compareFull: false,
                            datePreset: 'last90',
                            compareMode: 'none',
                            customCompareStart: '',
                            customCompareEnd: '',
                            isEditing: false,
                            loaded: false,
                            storedUserId: null,
                            previousLayouts: null,
                            preEditState: null,
                        })
                    }

                    try {
                        const layoutRes = await api(`/api/dashboard/layouts?type=${dashboardType}`, token)
                        if (layoutRes?.layouts) {
                            set({ availableLayouts: layoutRes.layouts })
                        }
                    } catch (e) { console.warn('[dashboard] Failed to load preset list:', e) }

                    // Goes through loadLayout (as Daily does) so currentLayoutId names the layout
                    // actually on screen, not one persisted from an earlier visit — Overwrite writes to that id.
                    if (!get().loaded) {
                        await get().loadLayout(token, userId, dashboardType, null)
                    }
                } catch (e) {
                    console.warn('[dashboard] loadFromServer failed:', e)
                }
            },

            loadLayout: async (token, userId, dashboardType = 'custom-range', id = null) => {
                try {
                    let endpoint = `/api/dashboard/layout?type=${dashboardType}`
                    if (id === 'system-default') endpoint += `&systemDefault=true`
                    else if (id) endpoint += `&id=${id}`

                    const layoutData = await api(endpoint, token)
                    if (layoutData?.layoutJson) {
                        try {
                            const parsed = JSON.parse(layoutData.layoutJson)
                            if (parsed.activeWidgets) {
                                set({
                                    layouts: parsed.layouts ?? generateDefaultLayouts(),
                                    activeWidgets: parsed.activeWidgets,
                                    widgetSettings: parsed.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS },
                                    primaryComparison: parsed.primaryComparison ?? 'none',
                                    compareFull: parsed.compareFull ?? false,
                                    datePreset: parsed.datePreset ?? 'last90',
                                    compareMode: parsed.compareMode ?? 'none',
                                    customCompareStart: parsed.customCompareStart ?? '',
                                    customCompareEnd: parsed.customCompareEnd ?? '',
                                    // Only a personal layout can be overwritten; the page reads a null id
                                    // as "System Default" rather than PUTting to the system layout's id.
                                    currentLayoutId: layoutData.source === 'user' ? (layoutData.id ?? null) : null,
                                    currentLayoutName: layoutData.name ?? null,
                                    currentLayoutIsDefault: layoutData.isDefault ?? false,
                                    currentLayoutUpdatedAt: layoutData.updatedAt ?? null,
                                    systemLayoutUpdatedAt: layoutData.systemUpdatedAt ?? null,
                                    loaded: true,
                                    storedUserId: userId,
                                })
                            }
                        } catch (e) {
                            console.warn('[dashboard] Failed to parse server layout JSON:', e, layoutData.layoutJson)
                        }
                    } else {
                        set({
                            currentLayoutId: null,
                            currentLayoutName: null,
                            currentLayoutIsDefault: false,
                            currentLayoutUpdatedAt: null,
                            systemLayoutUpdatedAt: null,
                        })
                    }
                    set((s) => s.loaded ? {} : { loaded: true, storedUserId: userId })
                } catch (e) {
                    console.warn('[dashboard] loadLayout failed:', e)
                }
            },

            // Save, revert and manage layouts: shared by all four dashboards (stores/layoutActions)
            ...createLayoutActions<DashboardLayoutState>(set, get, {
                dashboardType: 'custom-range',
                serialize: s => JSON.stringify({
                    layouts: s.layouts,
                    activeWidgets: s.activeWidgets,
                    widgetSettings: s.widgetSettings,
                    primaryComparison: s.primaryComparison,
                    compareFull: s.compareFull,
                    datePreset: s.datePreset,
                    compareMode: s.compareMode,
                    customCompareStart: s.customCompareStart,
                    customCompareEnd: s.customCompareEnd,
                }),
            }),

            saveAsSystemDefault: async (token, dashboardType = 'custom-range', overwriteUsers = false) => {
                const state = get()
                const payload = {
                    layouts: state.layouts,
                    activeWidgets: state.activeWidgets,
                    widgetSettings: state.widgetSettings,
                    primaryComparison: state.primaryComparison,
                    compareFull: state.compareFull,
                    datePreset: state.datePreset,
                    compareMode: state.compareMode,
                    customCompareStart: state.customCompareStart,
                    customCompareEnd: state.customCompareEnd,
                }
                const layoutJson = JSON.stringify(payload)
                await apiPut('/api/admin/dashboard/layout/default', token, {
                    layoutJson,
                    dashboardType,
                    overwriteUsers,
                })
            },

            hasUnsavedChanges: () => {
                const state = get()
                if (!state.isEditing || !state.preEditState) return false
                return (
                    JSON.stringify(state.activeWidgets) !== JSON.stringify(state.preEditState.activeWidgets) ||
                    JSON.stringify(state.layouts) !== JSON.stringify(state.preEditState.layouts) ||
                    JSON.stringify(state.widgetSettings) !== JSON.stringify(state.preEditState.widgetSettings) ||
                    state.primaryComparison !== state.preEditState.primaryComparison ||
                    state.compareFull !== state.preEditState.compareFull
                )
            },

            applyImportedLayout: (parsed) => {
                set((s) => ({
                    layouts: parsed.layouts ?? generateDefaultLayouts(),
                    activeWidgets: parsed.activeWidgets,
                    widgetSettings: parsed.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS },
                    primaryComparison: (parsed.primaryComparison as any) ?? 'none',
                    compareFull: parsed.compareFull ?? false,
                    datePreset: (parsed as any).datePreset ?? s.datePreset,
                    compareMode: (parsed as any).compareMode ?? s.compareMode,
                    customCompareStart: (parsed as any).customCompareStart ?? s.customCompareStart,
                    customCompareEnd: (parsed as any).customCompareEnd ?? s.customCompareEnd,
                    currentLayoutId: null,
                    currentLayoutName: null,
                    currentLayoutIsDefault: false,
                    layoutVersion: s.layoutVersion + 1,
                    preEditState: {
                        layouts: JSON.parse(JSON.stringify(parsed.layouts ?? generateDefaultLayouts())),
                        activeWidgets: [...parsed.activeWidgets],
                        widgetSettings: JSON.parse(JSON.stringify(parsed.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS })),
                        primaryComparison: (parsed.primaryComparison as any) ?? 'none',
                        compareFull: parsed.compareFull ?? false,
                        datePreset: (parsed as any).datePreset ?? s.datePreset,
                        compareMode: (parsed as any).compareMode ?? s.compareMode,
                        customCompareStart: (parsed as any).customCompareStart ?? s.customCompareStart,
                        customCompareEnd: (parsed as any).customCompareEnd ?? s.customCompareEnd,
                    },
                }))
            },
        }),
        {
            name: 'puresoft-custom-range-dashboard-layout',
            version: 3, // v3: Employee Performance added to the default widgets
            migrate: (persistedState: any, version: number) => {
                const state = persistedState as Partial<DashboardLayoutState>
                const cached = {
                    layouts: state.layouts ?? generateDefaultLayouts(),
                    activeWidgets: state.activeWidgets ?? [...DEFAULT_ACTIVE_WIDGETS],
                }
                return {
                    // Everything persisted carries over — the current preset's id and name included.
                    ...state,
                    ...(version < 3 ? appendEmployeePerformance(cached.layouts, cached.activeWidgets) : cached),
                    widgetSettings: state.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS },
                    primaryComparison: state.primaryComparison ?? 'none',
                    compareFull: state.compareFull ?? false,
                    datePreset: state.datePreset ?? 'last90',
                    compareMode: state.compareMode ?? 'none',
                    customCompareStart: state.customCompareStart ?? '',
                    customCompareEnd: state.customCompareEnd ?? '',
                    loaded: false,
                    storedUserId: state.storedUserId ?? null,
                    previousLayouts: null,
                    preEditState: null,
                }
            },
            partialize: (state) => ({
                layouts: state.layouts,
                activeWidgets: state.activeWidgets,
                widgetSettings: state.widgetSettings,
                primaryComparison: state.primaryComparison,
                compareFull: state.compareFull,
                datePreset: state.datePreset,
                compareMode: state.compareMode,
                customCompareStart: state.customCompareStart,
                customCompareEnd: state.customCompareEnd,
                storedUserId: state.storedUserId,
                currentLayoutId: state.currentLayoutId,
                currentLayoutName: state.currentLayoutName,
                currentLayoutIsDefault: state.currentLayoutIsDefault,
            }) as any,
        }
    )
)
